import {
  AssociationIDsFilter,
  BooleanFilter,
  CreatedAt,
  CreateItemGqlQuery,
  CreateItemGqlStatus,
  DateFilterType,
  DeleteItemGqlQuery,
  DeleteItemGqlStatus,
  FetchItemCacheKey,
  FetchItemFetched,
  FetchItemGqlQuery,
  FetchItemIsPlaceholderData,
  FetchItemsCacheKey,
  FetchItemsClearItemsFilters,
  FetchItemsEditItem,
  FetchItemsErrorMessage,
  FetchItemsFetched,
  FetchItemsFilterItems,
  FetchItemsGqlQuery,
  FetchItemsIsPlaceholderData,
  FetchItemsLimit,
  FetchItemsPage,
  FetchItemsPaginateItems,
  FetchItemsPrefetchItems,
  FetchItemsShowItem,
  FetchItemsSort,
  FetchItemsSortItems,
  FetchItemsTotalCount,
  ID,
  IdFilterType,
  Item,
  ItemGqlError,
  NumberFilterType,
  StringArrayFilter,
  TextFilterType,
  TrackTimeInItemGqlQuery,
  UpdateItemGqlQuery,
  UpdateItemGqlStatus,
  UUID
} from '../../types';
import { UserNanoID, UserUUID } from '../users/usersTypes';
import { ImageFile } from '../images/imagesTypes';

export type CategoryID = ID;
export type CategoryUUID = UUID;
export type CategoryName = string;
export type CategoryNameEn = string;
export type CategoryLocalizedName = string;
export type CategoryCreatedAt = CreatedAt;

export type CategoryParentID = CategoryID;
export type CategoryParentUUID = CategoryUUID;
export type CategoryParentName = CategoryName;
export type CategoryParentNameEn = CategoryNameEn;
export type CategoryParentLocalizedName = CategoryLocalizedName;

export type CategoryImageID = ID;
export type CategoryImageFile = ImageFile;

export enum CategoryFields {
  ID = 'id',
  UUID = 'uuid',
  NAME = 'name',
  CREATED_AT = 'createdAt'
}

export interface FetchCategoriesFilters {
  id?: IdFilterType;
  name?: TextFilterType;
  nameEn?: TextFilterType;
  nameUk?: TextFilterType;
  order?: NumberFilterType;
  categoriesCount?: NumberFilterType;
  pdfExport?: BooleanFilter;
  blocked?: BooleanFilter;
  createdAt?: DateFilterType;
  parentId?: IdFilterType;
  productClientIds?: AssociationIDsFilter;
  materialClientIds?: AssociationIDsFilter;
  lifestyleClientIds?: AssociationIDsFilter;
  scope?: StringArrayFilter;
  productCategoryIds?: AssociationIDsFilter;
  productParentCategoryIds?: AssociationIDsFilter;
}

export interface FetchFinCategoriesFiltersProductFilters {
  blocked?: BooleanFilter;
  brandId?: IdFilterType;
  colorId?: IdFilterType;
  favorite?: BooleanFilter;
  materialIds?: ID[];
  nda?: BooleanFilter;
  productTypeId?: IdFilterType;
  seasonalId?: IdFilterType;
  styleIds?: ID[];
  createdAt?: DateFilterType;
  productCategoryId?: IdFilterType;
  productClientId?: IdFilterType;
  productParentCategoryId?: IdFilterType;
  clientId?: IdFilterType;
}

export interface FetchFinCategoriesFiltersLifestyleFilters {
  blocked?: BooleanFilter;
  favorite?: BooleanFilter;
  materialIds?: ID[];
  nda?: BooleanFilter;
  styleIds?: ID[];
  createdAt?: DateFilterType;
  lifestyleCategoryId?: IdFilterType;
  lifestyleClientId?: IdFilterType;
  lifestyleParentCategoryId?: IdFilterType;
  gammaId?: IdFilterType;
  toneId?: IdFilterType;
  mainColorId?: IdFilterType;
  authorId?: IdFilterType;
  teamIds?: ID[];
  clientIds?: ID[];
}

export interface FetchFinCategoriesFilters {
  createdAt?: DateFilterType;
  facilityGroupsByProduct?: FetchFinCategoriesFiltersProductFilters;
  materialsByProduct?: FetchFinCategoriesFiltersProductFilters;
  seasonalByProduct?: FetchFinCategoriesFiltersProductFilters;
  id?: IdFilterType;
  parentId?: IdFilterType;
  scope?: TextFilterType;
  order?: NumberFilterType;
  pdfExport?: BooleanFilter;
  productClientIds?: AssociationIDsFilter;
  materialClientIds?: AssociationIDsFilter;
  lifestyleClientIds?: AssociationIDsFilter;
  name?: TextFilterType;
  productCategoryId?: IdFilterType;
  productParentCategoryId?: IdFilterType;
  productClientId?: IdFilterType;
  roomsByLifestyle?: FetchFinCategoriesFiltersLifestyleFilters;
  typesByLifestyle?: FetchFinCategoriesFiltersLifestyleFilters;
  typesByProduct?: FetchFinCategoriesFiltersProductFilters;
  parentFinCategoryId?: IdFilterType;
}

export enum FetchCategoriesSortableFields {
  ID = 'id',
  NAME = 'name',
  CREATED_AT = 'createdAt'
}

export enum FetchCategoriesFilterFields {
  NAME = 'name',
  CREATED_AT = 'createdAt'
}

export enum FetchCategoriesSortTypes {
  ID_ASC = 'ID_ASC',
  ID_DESC = 'ID_DESC',
  NAME_ASC = 'NAME_ASC',
  NAME_DESC = 'NAME_DESC',
  LOCALIZED_NAME_ASC = 'LOCALIZED_NAME_ASC',
  CREATED_AT_ASC = 'CREATED_AT_ASC',
  CREATED_AT_DESC = 'CREATED_AT_DESC'
}

export enum FetchCategoriesScopes {
  CATEGORY = 'clients_categories',
  CELLS = 'users_cells',
  CLIENT_MANAGER = 'client_manager',
  COLD_SOURCE = 'cold_source',
  COMPANY_REVENUE = 'company_revenue',
  COMPANY_SIZE = 'users_company_sizes',
  COMPLEXITIES = 'complexities',
  COUNTRIES = 'clients_countries',
  FACILITIES_RENDER_TYPES = 'facilities_render_types',
  FACILITY_COLORS = 'facilities_colors',
  FACILITY_MANUFACTURERS = 'facility_manufacturers',
  FACILITY_MATERIALS = 'facility_materials',
  FACILITY_STYLES = 'facility_styles',
  GROUPS = 'users_groups',
  INTEGRATION = 'users_integrations',
  LEAVE_OF_ABSENCE = 'leave_of_absence',
  LIFESTYLE_GROUPS = 'scene_rooms',
  LIFESTYLES_TAGS = 'lifestyles_tags',
  LIFESTYLES_TYPES = 'lifestyles_types',
  MANUAL_CLIENT_STATUS = 'users_manual_client_status',
  MARKETING_SOURCE = 'marketing_source',
  MATERIAL_BRANDS = 'material_brands',
  MATERIAL_CATEGORIES = 'material_categories',
  NATURE_OF_LEAD = 'nature_of_lead',
  PAYMENT_TYPE = 'payment_type',
  POSITION = 'user_position',
  PRODUCT_COLLECTIONS = 'facilities_collections',
  PRODUCT_GROUPS = 'facility_groups',
  PRODUCTS_FILE_VERSION_TAGS = 'facilities_file_version_tags',
  PRODUCTS_MISTAKE_TYPES = 'facilities_mistake_types',
  PRODUCTS_SEASONAL = 'products_seasonal',
  PRODUCTS_STATUSES = 'facilities_statuses',
  PRODUCTS_TAGS = 'products_tags',
  PRODUCTS_TYPE = 'products_types',
  PROFICIENCY_LEVEL = 'proficiency_level',
  PROJECT_MANAGERS = 'project_managers',
  PROJECTS_NAMES = 'projects_names',
  QUALIFICATIONS = 'users_qualifications',
  REAL_DECLINE_REASONS = 'real_decline_reason',
  REG_POSITION_ID = 'user_position',
  SCENE_ACCENT_COLORS = 'scene_accent_colors',
  SCENE_GAMMAS = 'scene_gammas',
  SCENE_MAIN_COLORS = 'scene_main_colors',
  SCENE_MATERIALS = 'scene_materials',
  SCENE_PERFORMERS = 'scene_performers',
  SCENE_STYLES = 'scene_styles',
  SCENE_TONES = 'scene_tones',
  SCENES_RENDER_TYPES = 'scenes_render_types',
  SCOPE_OF_WORKS = 'scope_of_works',
  SOURCE = 'clients_sources',
  SPECIALIZATIONS = 'users_specializations',
  STATES = 'clients_states',
  SUB_CATEGORIES = 'users_sub_categories',
  SUB_CUSTOM_CLIENT_STATUS = 'sub_custom_client_status',
  T_A_T = 't_a_t',
  TEAMS_TAGS = 'teams_tags',
  THREE_D_SERVICES = '3d_services',
  URL = 'url',
  WORKFLOW_TYPE = 'workflow_type',
  WARNING = 'warning',
  FILE_TAGS = 'file_tags'
}

export type FetchCategoriesGqlQuery = FetchItemsGqlQuery;
export type FetchCategoryGqlQuery = FetchItemGqlQuery;
export type CreateCategoryGqlQuery = CreateItemGqlQuery;
export type UpdateCategoryGqlQuery = UpdateItemGqlQuery;
export type DeleteCategoryGqlQuery = DeleteItemGqlQuery;
export type DownloadCategoryResultsGqlQuery = UpdateItemGqlQuery;
export type TrackTimeInCategoryGqlQuery = TrackTimeInItemGqlQuery;

export type CreateCategoryGqlStatus = CreateItemGqlStatus;
export type UpdateCategoryGqlStatus = UpdateItemGqlStatus;
export type DeleteCategoryGqlStatus = DeleteItemGqlStatus;
export type DownloadCategoryResultsGqlStatus = UpdateItemGqlStatus;
export type CategoryGqlError = ItemGqlError;

export type FetchCategoriesCacheKey = FetchItemsCacheKey;
export type FetchCategoryCacheKey = FetchItemCacheKey;

export type FetchCategoriesFetched = FetchItemsFetched;
export type FetchCategoriesErrorMessage = FetchItemsErrorMessage;
export type FetchCategoriesIsPlaceholderData = FetchItemsIsPlaceholderData;
export type FetchCategoriesPage = FetchItemsPage;
export type FetchCategoriesLimit = FetchItemsLimit;
export type FetchCategoriesSort = FetchItemsSort; // FetchCategoriesSortTypes[];
export type FetchCategoriesTotalCount = FetchItemsTotalCount;
export type FetchCategoriesSortCategories = FetchItemsSortItems;
export type FetchCategoriesPaginateCategories = FetchItemsPaginateItems;
export type FetchCategoriesPrefetchCategories = FetchItemsPrefetchItems;
export type FetchCategoriesShowCategory = FetchItemsShowItem;
export type FetchCategoriesEditCategory = FetchItemsEditItem;

export type FetchCategoryFetched = FetchItemFetched;
export type FetchCategoryIsPlaceholderData = FetchItemIsPlaceholderData;

export type FetchCategoriesFilterCategories =
  FetchItemsFilterItems<FetchCategoriesFilters>;
export type FetchCategoriesClearCategoriesFilters = FetchItemsClearItemsFilters;
// Deprecated

export type FetchCategoriesFiltersFunc = (
  nextFilters: FetchCategoriesFilters
) => void;

// export type FetchCategoriesSort = FetchCategoriesSortItem[];

export interface CategoryType extends Item {
  blocked: boolean;
  name: string;
  nameEn: string;
  nameUk: string;
  order: number;
  pdfExport: boolean;
  categoriesCount: number;
  section: {
    uuid: UUID;
    name: string;
  };
  parent: {
    uuid: UUID;
    localizedName: string;
  };
  user: {
    uuid: UserUUID;
    nanoId: UserNanoID;
    fullName: string;
    image: {
      uuid: UUID;
      file: string;
    };
  };
}

export type CategoriesType = CategoryType[];
